import { Alert, Button, Flex, Form, Typography } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { TextInput } from "../../../components/Input/TextInput.tsx"
import {
  checkEducationVerificationCode,
  sendEducationVerificationCode
} from "../api/public-education.services"

const { Title, Text } = Typography

type Step = "email" | "code"

interface EmailFormValues {
  email: string
}

interface CodeFormValues {
  code: string
}

interface VerifyEmailFormProps {
  session: string
  /** The next session `verify/check` issues, already carrying `verified: true`. */
  onVerified: (session: string) => void
}

/**
 * Screen 7's gate for a `requires_verification` link: an email field, a
 * neutral "if that matches" send confirmation (the backend never says
 * whether the address was recognized — see `PublicVerifySendView`), then a
 * 6-digit code. Both steps are plain antd `Form`s so Tab moves between
 * fields and Enter submits, the standing rule for every form with fields and
 * a submit action in this app.
 */
export function VerifyEmailForm({ session, onVerified }: VerifyEmailFormProps) {
  const { t } = useTranslation()
  const [emailForm] = Form.useForm<EmailFormValues>()
  const [codeForm] = Form.useForm<CodeFormValues>()
  const [step, setStep] = useState<Step>("email")
  const [email, setEmail] = useState("")
  const [sending, setSending] = useState(false)
  const [sendFailed, setSendFailed] = useState(false)
  const [codeSent, setCodeSent] = useState(false)
  const [checking, setChecking] = useState(false)
  const [wrongCode, setWrongCode] = useState(false)

  const submitEmail = async ({ email: submittedEmail }: EmailFormValues) => {
    setSending(true)
    setSendFailed(false)
    const response = await sendEducationVerificationCode(session, submittedEmail)
    setSending(false)

    // The endpoint always answers 202 whether or not the address matched —
    // only a real transport failure (network, 5xx) is worth telling apart
    // from that, since retrying THAT is the one thing that can actually help.
    if (!response.ok && response.status === 0) {
      setSendFailed(true)
      return
    }
    setEmail(submittedEmail)
    setCodeSent(true)
    setWrongCode(false)
    setStep("code")
  }

  const submitCode = async ({ code }: CodeFormValues) => {
    setChecking(true)
    setWrongCode(false)
    const response = await checkEducationVerificationCode(session, code)
    setChecking(false)

    if (response.ok && response.data) {
      onVerified(response.data.session)
      return
    }
    setWrongCode(true)
    codeForm.resetFields(["code"])
  }

  const useDifferentEmail = () => {
    setStep("email")
    setCodeSent(false)
    setWrongCode(false)
    codeForm.resetFields()
  }

  if (step === "email") {
    return (
      <Flex vertical gap={16} data-testid={"project-education-verify-email"}>
        <Title level={5} className={"!m-0"}>
          {t("projectEducation.publicPage.verify.title")}
        </Title>
        <Form form={emailForm} layout={"vertical"} onFinish={(values) => void submitEmail(values)}>
          <Form.Item<EmailFormValues>
            name={"email"}
            label={t("projectEducation.publicPage.verify.emailLabel")}
            rules={[{ required: true, type: "email" }]}
          >
            <TextInput
              type={"email"}
              disableAutoCapitalize
              autoFocus
              data-testid={"project-education-verify-email-input"}
            />
          </Form.Item>
          {sendFailed && (
            <Alert
              className={"mb-3"}
              type={"error"}
              showIcon
              message={t("projectEducation.publicPage.verify.sendFailed")}
            />
          )}
          <Button
            type={"primary"}
            htmlType={"submit"}
            loading={sending}
            data-testid={"project-education-send-code"}
          >
            {t("projectEducation.publicPage.verify.sendCode")}
          </Button>
        </Form>
      </Flex>
    )
  }

  return (
    <Flex vertical gap={16} data-testid={"project-education-verify-code"}>
      <Title level={5} className={"!m-0"}>
        {t("projectEducation.publicPage.verify.title")}
      </Title>
      {codeSent && (
        <Text type={"secondary"}>{t("projectEducation.publicPage.verify.codeSentNeutral")}</Text>
      )}
      <Form form={codeForm} layout={"vertical"} onFinish={(values) => void submitCode(values)}>
        <Form.Item<CodeFormValues>
          name={"code"}
          label={t("projectEducation.publicPage.verify.codeLabel")}
          rules={[{ required: true, len: 6 }]}
        >
          <TextInput
            inputMode={"numeric"}
            maxLength={6}
            disableAutoCapitalize
            autoFocus
            data-testid={"project-education-verify-code-input"}
          />
        </Form.Item>
        {wrongCode && (
          <Alert
            className={"mb-3"}
            type={"error"}
            showIcon
            message={t("projectEducation.publicPage.verify.wrongCode")}
          />
        )}
        <Flex gap={8} wrap>
          <Button
            type={"primary"}
            htmlType={"submit"}
            loading={checking}
            data-testid={"project-education-verify-submit"}
          >
            {t("projectEducation.publicPage.verify.verify")}
          </Button>
          <Button
            type={"link"}
            onClick={() => void submitEmail({ email })}
            disabled={sending}
          >
            {t("projectEducation.publicPage.verify.resend")}
          </Button>
          <Button type={"link"} onClick={useDifferentEmail}>
            {t("projectEducation.publicPage.verify.useDifferentEmail")}
          </Button>
        </Flex>
      </Form>
    </Flex>
  )
}
